/**
 * Bulk Action Toolbar Component
 *
 * Displays when strategies are selected, providing bulk action buttons
 * for pause, resume, reload, update budget, and update profit tiers.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { traderTheme as theme } from './traderDashboardTheme';
import type { BulkOperationResponse } from '../../contexts/TraderDataContextLegacy';
import type { ProfitTier } from '../../types/strategy';

export interface BulkActionToolbarProps {
  /** Currently selected strategy IDs */
  selectedIds: Set<string>;
  /** Total number of strategies available for selection */
  totalCount: number;
  /** Callback to clear selection */
  onClearSelection: () => void;
  /** Callback to select all */
  onSelectAll: () => void;
  /** Bulk pause handler */
  onBulkPause: (strategyIds: string[]) => Promise<BulkOperationResponse>;
  /** Bulk resume handler */
  onBulkResume: (strategyIds: string[]) => Promise<BulkOperationResponse>;
  /** Bulk reload handler */
  onBulkReload: (strategyIds: string[]) => Promise<BulkOperationResponse>;
  /** Opens bulk budget dialog */
  onOpenBudgetDialog: () => void;
  /** Opens bulk profit tiers dialog */
  onOpenProfitTiersDialog: () => void;
  /** Opens bulk hedge config dialog */
  onOpenHedgeConfigDialog: () => void;
}

type BulkAction = 'pause' | 'resume' | 'reload' | 'budget' | 'profit_tiers' | 'hedge_config';

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedIds,
  totalCount,
  onClearSelection,
  onSelectAll,
  onBulkPause,
  onBulkResume,
  onBulkReload,
  onOpenBudgetDialog,
  onOpenProfitTiersDialog,
  onOpenHedgeConfigDialog,
}) => {
  const { t } = useTranslation();
  const [loadingAction, setLoadingAction] = useState<BulkAction | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const selectedCount = selectedIds.size;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  const handleBulkAction = async (
    action: BulkAction,
    handler: (ids: string[]) => Promise<BulkOperationResponse>
  ) => {
    if (selectedCount === 0) return;

    setLoadingAction(action);
    try {
      const result = await handler(Array.from(selectedIds));

      if (result.status === 'ok') {
        setSnackbar({
          open: true,
          message: `Successfully ${action}d ${result.succeeded} strategies`,
          severity: 'success',
        });
        onClearSelection();
      } else if (result.succeeded > 0) {
        setSnackbar({
          open: true,
          message: `${result.succeeded} succeeded, ${result.failed} failed`,
          severity: 'warning',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Failed to ${action} strategies`,
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setLoadingAction(null);
    }
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          backgroundColor: 'rgba(88, 166, 255, 0.15)',
          borderRadius: 1,
          border: `1px solid ${theme.accent}`,
          mb: 1,
        }}
      >
        {/* Selection Count */}
        <Typography
          variant="body2"
          sx={{
            color: theme.text.primary,
            fontWeight: 600,
          }}
        >
          {t('traderDashboard.bulk.selected', { count: selectedCount })}
        </Typography>

        {/* Select All Button */}
        {!allSelected && (
          <Button
            size="small"
            variant="text"
            onClick={onSelectAll}
            sx={{
              color: theme.accent,
              textTransform: 'none',
              minWidth: 'auto',
              px: 1,
              '&:hover': {
                backgroundColor: 'rgba(88, 166, 255, 0.1)',
              },
            }}
          >
            {t('traderDashboard.bulk.selectAll', { count: totalCount })}
          </Button>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={loadingAction === 'pause' ? <CircularProgress size={14} /> : <PauseIcon />}
            onClick={() => handleBulkAction('pause', onBulkPause)}
            disabled={loadingAction !== null}
            sx={{
              borderColor: theme.warning,
              color: theme.warning,
              '&:hover': {
                borderColor: theme.warning,
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
              },
            }}
          >
            {t('traderDashboard.actions.pause')}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={loadingAction === 'resume' ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            onClick={() => handleBulkAction('resume', onBulkResume)}
            disabled={loadingAction !== null}
            sx={{
              borderColor: theme.profit,
              color: theme.profit,
              '&:hover': {
                borderColor: theme.profit,
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
              },
            }}
          >
            {t('traderDashboard.actions.resume')}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={loadingAction === 'reload' ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => handleBulkAction('reload', onBulkReload)}
            disabled={loadingAction !== null}
            sx={{
              borderColor: theme.accent,
              color: theme.accent,
              '&:hover': {
                borderColor: theme.accent,
                backgroundColor: 'rgba(88, 166, 255, 0.1)',
              },
            }}
          >
            {t('traderDashboard.actions.reload')}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachMoneyIcon />}
            onClick={onOpenBudgetDialog}
            disabled={loadingAction !== null}
            sx={{
              borderColor: theme.text.secondary,
              color: theme.text.secondary,
              '&:hover': {
                borderColor: theme.text.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {t('traderDashboard.actions.updateBudget')}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<TrendingUpIcon />}
            onClick={onOpenProfitTiersDialog}
            disabled={loadingAction !== null}
            sx={{
              borderColor: theme.text.secondary,
              color: theme.text.secondary,
              '&:hover': {
                borderColor: theme.text.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {t('traderDashboard.actions.updateProfitTiers')}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<WarningIcon />}
            onClick={onOpenHedgeConfigDialog}
            disabled={loadingAction !== null}
            sx={{
              borderColor: theme.text.secondary,
              color: theme.text.secondary,
              '&:hover': {
                borderColor: theme.text.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {t('traderDashboard.actions.updateHedgeConfig')}
          </Button>
        </Box>

        {/* Clear Selection Button */}
        <Button
          size="small"
          startIcon={<CloseIcon />}
          onClick={onClearSelection}
          sx={{
            color: theme.text.secondary,
            '&:hover': {
              color: theme.text.primary,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          {t('traderDashboard.bulk.clear')}
        </Button>
      </Box>

      {/* Snackbar for operation results */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkActionToolbar;
